/**
 * Composition root for logging: mints the stable per-browser `sessionId`,
 * builds one `Logger` for the tree, adopts the top-level
 * `infra.error.unhandled` boundary, and mounts the two reporters — `WebVitals`
 * and `PerformanceTelemetry`. Mounted once in apps/retells/src/main.tsx, wrapping
 * `<App />` inside the BrowserRouter.
 *
 * It renders no DOM of its own, which is what lets it sit in the client tree
 * without changing the markup: a test can render `<App />` under a StaticRouter
 * with no provider around it and get the same output, because everything this
 * component contributes is context and null-rendering reporters.
 *
 * This provider does NOT install the error boundary — ./earlyErrorCapture
 * does, at module scope, long before React commits its first pass. All this
 * provider does is hand that boundary a `Logger` once one exists, so events
 * caught after mount carry this component's correlation context. Attaching a
 * second pair of listeners here would double-report every failure.
 *
 * `sessionId` is minted in a `useState` initializer rather than an effect, so
 * it is present on the very first event this provider reports — not just on
 * events fired after a first effect pass.
 *
 * User identity deliberately does not live in this provider. Moving it below
 * an auth provider would put this client-only composition root and its
 * localStorage/error-boundary responsibilities behind an async gate; a mutable
 * context bridge would also re-render every logging consumer when auth
 * resolves. Authenticated consumers instead call `useLogger({ userId })`.
 */
import { PerformanceTelemetry, WebVitals } from "@bindery/logging";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { recordLanding } from "../lib/attribution";
import { createLogger, makeContextProvider } from "./backend";
import { setUnhandledErrorReporter } from "./earlyErrorCapture";
import { LoggerContext } from "./LoggerContext";
import { getSessionId } from "./sessionId";

export function LoggerProvider({ children }: { children: ReactNode }) {
  const [sessionId] = useState(getSessionId);
  // Resolved in a `useState` initializer rather than an effect, so the lead id
  // is on the very first event this browser emits — including one fired during
  // the initial render. `recordLanding` is idempotent, so StrictMode's second
  // invocation costs nothing, and it contains its own failures: this provider
  // sits above the router, so a capture that threw here would blank the app
  // rather than lose one attribution row.
  const [leadId] = useState(recordLanding);

  const log = useMemo(
    () => createLogger(makeContextProvider(sessionId, undefined, leadId)),
    [sessionId, leadId],
  );

  // Adopt the module-scope boundary rather than installing one. The disposer
  // hands it back on unmount (and before re-adopting with a new `log`), which
  // returns it to ./earlyErrorCapture's own pre-mount path — deaf is never one
  // of the states this boundary can be in.
  useEffect(
    () =>
      setUnhandledErrorReporter((error, source) => {
        log.error("infra.error.unhandled", error, { source });
      }),
    [log],
  );

  return (
    <LoggerContext value={{ sessionId, leadId: leadId || undefined }}>
      <WebVitals logger={log} />
      <PerformanceTelemetry logger={log} />
      {children}
    </LoggerContext>
  );
}
